/**
 * Slider — input range + número editável.
 *
 * Pattern do mockup 20: "slider-pair" com track verde, handle bege, valor
 * numérico à direita editável. Usado nas 3 sliders da Etapa 1, nos 4
 * blocos de R$/km/L da Etapa 2, e em horas/resíduos da Etapa 3.
 *
 * O número e o slider são sincronizados — editar o input dispara onChange
 * com clamp [min, max]; mover o slider só ajusta o input visualmente.
 *
 * Props:
 *  - value: valor atual (controlled)
 *  - onChange: callback com number já clamped
 *  - min/max/step: limites do range (defaults razoáveis pro dominio do mockup)
 *  - unit: sufixo opcional (ex: "horas", "kg", "%") — só visual, não afeta value
 */
import { useId } from 'react';

interface SliderProps {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  error?: string;
  /** Quando true, mostra "%" como sufixo e força max=100 step=1. */
  asPercent?: boolean;
}

export function Slider({
  label,
  hint,
  value,
  onChange,
  min = 0,
  max,
  step,
  unit,
  error,
  asPercent,
}: SliderProps) {
  const id = useId();

  const effMax = asPercent ? 100 : (max ?? 1000);
  const effStep = asPercent ? 1 : (step ?? 1);
  const effUnit = asPercent ? '%' : unit;

  // Clamp + parse defensivo (input numérico aceita '' temporariamente).
  const setSafe = (raw: number) => {
    if (Number.isNaN(raw)) return onChange(0);
    if (raw < min) return onChange(min);
    if (raw > effMax) return onChange(effMax);
    onChange(raw);
  };

  // Percentual do fill — pra colorir track. Se min=0 max=100, value=50 → 50%.
  const fillPct = effMax > min ? ((value - min) / (effMax - min)) * 100 : 0;

  return (
    <div className="py-3.5 border-b border-dashed border-areia/60 last:border-b-0">
      <label
        htmlFor={id}
        className="block text-[14px] text-ink leading-snug mb-3.5"
      >
        {label}
      </label>
      <div
        className={
          'grid gap-4 items-center ' +
          (effUnit ? 'grid-cols-[1fr_170px]' : 'grid-cols-[1fr_140px]')
        }
      >
        {/* Track + fill gradient bronze→olive (mockup 20 .slider/.slider-fill) */}
        <div className="relative h-1.5 rounded-full bg-cream cursor-pointer">
          <div
            className="absolute inset-y-0 left-0 rounded-full pointer-events-none"
            style={{
              width: `${fillPct}%`,
              // Mockup 20: gradient bronze→floresta (cobre 9D764F → olive 4A4C39).
              background: 'linear-gradient(90deg, #9D764F, #4A4C39)',
            }}
          />
          <input
            id={id}
            type="range"
            min={min}
            max={effMax}
            step={effStep}
            value={value}
            onChange={(e) => setSafe(Number(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            aria-label={label}
          />
          {/* Handle 18×18, white border, shadow — replica mockup .slider-handle */}
          <div
            className="absolute top-1/2 -translate-y-1/2 size-[18px] rounded-full bg-floresta border-[3px] border-cream shadow-[0_2px_8px_rgba(74,76,57,0.32)] pointer-events-none"
            style={{ left: `calc(${fillPct}% - 9px)` }}
          />
        </div>
        {/* Input numérico em monospaced à direita (mockup .num-input) — coluna
          mais larga (170px com unit, 140px sem) pra caber valores 5-6 dígitos
          tipo 50000 sem truncar. Com asPercent fica menor (3 dígitos basta). */}
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            value={value}
            min={min}
            max={effMax}
            step={effStep}
            onChange={(e) => setSafe(Number(e.target.value))}
            className="block w-full rounded-lg border border-areia bg-cream/60 px-3 py-2 text-sm font-mono text-floresta text-right tabular-nums focus:outline-none focus:border-bronze focus:bg-cream"
          />
          {effUnit && (
            <span className="font-mono text-xs text-tabaco whitespace-nowrap" aria-hidden>
              {effUnit}
            </span>
          )}
        </div>
      </div>
      {hint && !error && <p className="text-2xs text-ink-subtle mt-1.5 font-mono">{hint}</p>}
      {error && <p className="text-2xs text-cobre-700 mt-1.5">{error}</p>}
    </div>
  );
}
